import { Alert, styled, Typography } from '@mui/material';
import React from 'react';
import { ILabelValuePanel } from '../Dashboard.model';
import { Box, Paper, Skeleton, Stack } from '@mui/material';
import { LayoutItem } from 'react-grid-layout';

interface LabelValuePanelProps {
  panel: ILabelValuePanel;
  layout?: LayoutItem | undefined;
}
function LabelValuePanel(props: LabelValuePanelProps) {
  const { panel, layout } = props;
  const { title, subtitle, icon, content } = panel;
  const isSmall = layout ? layout?.w <= 2 || layout.h <= 1 || !icon : !icon;
  if (panel.loading) {
    return <Skeleton variant="rectangular" width="100%" height="100%" />;
  }
  if (panel.error) {
    return <Alert severity="error">{panel.error}</Alert>;
  }

  return isSmall ? (
    <Stack spacing={0.5} sx={{ height: '100%', width: '100%' }}>
      <Typography sx={{ fontWeight: 900 }}>{title}</Typography>
      {subtitle && (
        <Typography variant="body2" color="text.secondary" fontSize={'0.6em'}>
          {subtitle}
        </Typography>
      )}
      <Paper
        elevation={8}
        sx={{
          height: '100%',
          width: '100%',
          flex: '1 1 auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: 'none',
          borderRadius: '18px',
          fontSize: '2em',
          fontWeight: 800,
          backgroundColor: (theme) =>
            theme.palette.mode === 'dark'
              ? theme.palette.background.paper
              : theme.palette.background.default,
          overflow: 'hidden',
        }}
      >
        {content}
      </Paper>
    </Stack>
  ) : (
    <Stack
      direction={'row'}
      spacing={2}
      sx={{ height: '100%', maxWidth: '100%', width: '100%' }}
    >
      {/* Left */}
      <Stack
        spacing={0.25}
        alignItems="flex-start"
        height={'100%'}
        flexGrow={1}
        width={'min-content'}
      >
        <div>
          <Typography sx={{ fontWeight: 900, fontSize: '0.8em' }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography
              variant="body2"
              color="text.secondary"
              fontSize={'0.6em'}
            >
              {subtitle}
            </Typography>
          )}
        </div>
        {/* Icon block */}
        <Box
          sx={{
            flex: '1 1 auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100%',
            p: 2,
            opacity: 0.95,
          }}
        >
          <Box
            sx={{
              maxWidth: '120px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '4rem',
            }}
          >
            {icon}
          </Box>
        </Box>
      </Stack>

      {/* right */}
      <Paper
        elevation={8}
        sx={{
          height: '100%',
          maxWidth: '60%',
          minWidth: '60%',
          flexGrow: 1.6,
          boxShadow: 'none',
          borderRadius: '18px',
          fontSize: '3em',
          fontWeight: 800,
          backgroundColor: (theme) =>
            theme.palette.mode === 'dark'
              ? theme.palette.background.paper
              : theme.palette.background.default,
          overflow: 'hidden',
        }}
      >
        {content}
      </Paper>
    </Stack>
  );
}

export const DefaultRightContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'success',
})<{ success: boolean }>(({ success, theme }) => ({
  width: '100%',
  height: '100%',
  minWidth: 120,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  color: success ? theme.palette.success.main : theme.palette.text.primary,
}));

export default LabelValuePanel;
